import { PencilIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { randomUUID } from "crypto";
import { useEffect, useState } from "react";
import { Control, useFieldArray, useFormContext } from "react-hook-form";
import { Modal } from "src/components/DialogComponent";
import { SearchUI } from "src/components/ui/Search";
import TGButton from "src/components/ui/TGButton";
import { Option, TGSelectPickerByList } from "src/components/ui/TGSelect";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";

import { isEqual } from "lodash";
import { useAdditionalFieldsStore } from "../meetingsPage/stores/additionalFieldsStore";
import TGCheckbox from "src/components/ui/TGCheckbox";
import { Card } from "src/components/ui/Card";
import { twMerge } from "tailwind-merge";
const additionalList: FieldType[] = [
  { id: "1", name: "Выбор локации", type: "location" },
  { id: "2", name: "Выбор отдела", type: "department" },
  { id: "3", name: "Выбор участка", type: "sector" },
  { id: "4", name: "Выбор сотрудника", type: "employee" },
  { id: "5", name: "Выбор даты", type: "date" },
  { id: "6", name: "Одиночный выбор из списка", type: "single" },
  { id: "7", name: "Свободный текст", type: "free" },
  { id: "8", name: "Множественный выбор", type: "many" },
  { id: "9", name: "Пятибалльная шкала", type: "grade" },
];

const typeMap = {
  location: "Выбор локации",
  department: "Выбор отдела",
  sector: "Выбор участка",
  employee: "Выбор сотрудника",
  date: "Выбор даты",
  single: "Одиночный выбор из списка:",
  free: "Свободный текст",
  many: "Множественный выбор:",
  grade: "Пятибалльная шкала",
};

export type FieldType = {
  id: string;
  name: string;
  type: string;
  value?: any;
  options?: Option[];
};

function CheckListAdditionalMeetingFields({ zIndex = 50 }: { zIndex?: number }) {
  const { fields } = useAdditionalFieldsStore();

  const [open, setOpen] = useState(false);

  function toggleOpen(value: boolean) {
    setOpen(value);
  }

  return (
    <>
      <AdditionalMeetingFieldsList fields={fields} />
      {/* Кнопка открытия модалки доп полей */}
      {open ? (
        <div className="p-5 rounded-lg bg-gray2 mt-2.5">
          <AdditionalModal zIndex={zIndex} open={open} toggleOpen={toggleOpen} />
        </div>
      ) : null}
      {!open && (
        <TGButton
          type="button"
          className="w-full mt-2.5"
          icon={<PlusCircleIcon width={20} height={20} />}
          onClick={() => toggleOpen(true)}
        >
          Добавить вопрос
        </TGButton>
      )}
    </>
  );
}

export default CheckListAdditionalMeetingFields;

const getDefaultTypeField = (defaultType: string) => additionalList.filter((field) => field.type === defaultType);

function isSelectFields(type: FieldType) {
  return type.id === "6" || type.id === "8";
}

function isCorrectOption(options: Option[]) {
  return options.some((option) => option.isCorrect);
}

function isEmptyOption(options: Option[]) {
  return options.some((option) => option.name === "");
}

function AdditionalModal({
  open,
  toggleOpen,
}: {
  zIndex?: number;
  open: boolean;
  toggleOpen: (value: boolean) => void;
}) {
  const { append } = useAdditionalFieldsStore();
  const { watch } = useFormContext();

  const [questionTitle, setQuestionTitle] = useState("");
  const [typeField, setTypeField] = useState<FieldType[]>([additionalList[0]]);
  const [options, setOptions] = useState<Option[]>([]);
  const [isRequired, setIsRequired] = useState(false);

  function changeTypeField(type: FieldType[]) {
    setTypeField(type);
  }

  function resetFields() {
    setTypeField([additionalList[0]]);
  }

  function addField() {
    const submitField = {
      name: questionTitle,
      type: typeField[0].type, // type это сам строковый тип
      value: typeField[0],
      options: isSelectFields(typeField[0]) ? options : [],
      isRequired: isRequired,
    };

    append(submitField);
    resetFields();

    toggleOpen(false);
  }

  function cancel() {
    resetFields();
    toggleOpen(false);
  }

  watch("additionalField");

  useEffect(() => {
    if (isSelectFields(typeField[0])) {
      setOptions([{ name: "", id: crypto.randomUUID() }]);
    } else {
      setOptions([]);
    }
  }, [typeField[0].id]);

  return (
    <>
      {open ? (
        <FlexCol className="shrink">
          <div className="w-full">
            <div className="p-3.5 bg-white rounded-lg shadow-custom mb-5">
              <TGCheckbox isChecked={isRequired} onChange={() => setIsRequired(!isRequired)}>
                Обязательный вопрос
              </TGCheckbox>
            </div>
            <div>
              <TGSelectPickerByList<FieldType>
                values={typeField}
                onChange={changeTypeField}
                fetchKey="additionalField"
                classNames={{
                  selectClassName: "!px-5",
                  labelClassName: "text-base font-normal",
                  dropdownClassName: "w-full",
                }}
                searchPlaceholder="Выберите тип поля"
                dataField={{
                  placeholder: "Выберите тип поля",
                  type: "peoplePicker",
                  require: true,
                  title: "Тип вопроса",
                  width: "w-full",
                  key: "additionalField",
                  single: true,
                  hideRemoveButton: true,
                }}
                list={additionalList}
                errors={{}}
              />
            </div>
            <div className="w-full">
              <QuestionTitle value={questionTitle} onChange={setQuestionTitle} />
              {isSelectFields(typeField[0]) && (
                <div className="py-5 rounded-lg text-primaryGray">
                  <OptionsInput options={options} setOptions={(options) => setOptions(options)} />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between space-x-5">
            <TGButton type="button" className="w-1/2" onClick={cancel}>
              Отмена
            </TGButton>
            <TGButton
              type="button"
              color="bluedark"
              disabled={
                !typeField.length ||
                !questionTitle.length ||
                (isSelectFields(typeField[0]) && (!isCorrectOption(options) || isEmptyOption(options)))
              }
              onClick={addField}
              className="w-1/2"
            >
              Сохранить вопрос
            </TGButton>
          </div>
        </FlexCol>
      ) : null}
    </>
  );
}

function QuestionTitle({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  function changeQuestionTitle(value: string) {
    onChange(value);
  }

  return (
    <div className="flex flex-col gap-y-2.5 w-full mb-5">
      <div>
        <p className="text-base text-black">Формулировка вопроса</p>
      </div>
      <div>
        <SearchUI
          name={"text"}
          defaultValue={value}
          onClick={(e: string) => {
            changeQuestionTitle(e);
          }}
          delay={0}
          require={true}
          placeholder="Введите формулировку вопроса"
          search={false}
        />
      </div>
    </div>
  );
}

function OptionsInput({ options, setOptions }: { options: Option[]; setOptions: (options: Option[]) => void }) {
  function addOption() {
    setOptions([...options, { name: "", id: crypto.randomUUID() }]);
  }

  function addOptionNotApplicable() {
    setOptions([...options, { name: "Не применимо", id: crypto.randomUUID() }]);
  }

  function changeOption(id: string, value: string, isCorrect: boolean, isWithAction: boolean) {
    const newOptions = options.map((o) => {
      return o.id === id ? { ...o, name: value, isCorrect, withAction: isWithAction } : o;
    });
    setOptions(newOptions);
  }

  function removeOption(id: string) {
    const newOptions = options.filter((o) => o.id !== id);
    setOptions(newOptions);
  }

  return (
    <>
      {options.length
        ? options.map((option) => (
            <>
              <p
                className={twMerge(
                  "mb-2.5 text-base text-black",
                  option.name === "Не применимо" ? "text-darkgray" : ""
                )}
              >
                Вариант ответа
              </p>
              <OptionInput
                key={option.id}
                option={option}
                changeOption={changeOption}
                removeOption={removeOption}
                isNotApplicable={option.name === "Не применимо"}
              />
            </>
          ))
        : null}
      <TGButton
        type="button"
        onClick={addOption}
        className="text-[#637381] w-full mb-5"
        icon={<PlusCircleIcon width={20} height={20} />}
      >
        Добавить вариант ответа
      </TGButton>
      {options.find((option) => option.name === "Не применимо") ? null : (
        <TGButton
          type="button"
          onClick={addOptionNotApplicable}
          className="text-[#637381] w-full"
          icon={<PlusCircleIcon width={20} height={20} />}
        >
          Добавить вариант ответа "Не применимо"
        </TGButton>
      )}
    </>
  );
}

function OptionInput({
  option,
  changeOption,
  removeOption,
  isNotApplicable,
}: {
  option: Option;
  changeOption: (id: string, value: string, isCorrect: boolean, isWithAction: boolean) => void;
  removeOption: (value: string) => void;
  isNotApplicable: boolean;
}) {
  function handleIsCorrect(next: boolean) {
    changeOption(option.id, option.name, next, option.withAction ?? false);
  }

  function handleWithAction(next: boolean) {
    changeOption(option.id, option.name, option.isCorrect ?? false, next);
  }

  return (
    <div className="flex flex-nowrap items-center pb-5 space-x-5">
      <SearchUI
        disabled={isNotApplicable}
        name={option.name}
        defaultValue={option.name}
        onClick={(e: string) => {
          changeOption(option.id, e, option.isCorrect ?? false, option.withAction ?? false);
        }}
        delay={0}
        require={true}
        placeholder="Введите вариант ответа"
        search={false}
      />
      {isNotApplicable ? null : (
        <>
          <div className="bg-white h-[46px] rounded-lg shadow-custom items-center justify-center flex shrink-0 hover:bg-gray-100 hover px-3.5">
            <TGCheckbox
              isChecked={option.isCorrect ?? false}
              onChange={(e) => handleIsCorrect(e.target.checked)}
              className="w-full h-full"
            >
              Правильный ответ
            </TGCheckbox>
          </div>
          <div className="bg-white h-[46px] rounded-lg shadow-custom items-center justify-center flex shrink-0 hover:bg-gray-100 hover px-3.5">
            <TGCheckbox
              isChecked={option.withAction ?? false}
              onChange={(e) => handleWithAction(e.target.checked)}
              className="w-full h-full"
            >
              Требуется действие
            </TGCheckbox>
          </div>
        </>
      )}
      <div className="bg-white w-[46px] h-[46px] rounded-lg shadow-custom items-center justify-center flex shrink-0 hover:bg-gray-100 hover">
        <TGButton
          onClick={() => removeOption(option.id)}
          iconOnly={true}
          icon={<TrashIcon width={20} height={20} />}
          color="white"
          className="w-full h-full"
        />
      </div>
    </div>
  );
}

function AdditionalMeetingFieldsList({ fields }: { fields: FieldType[] }) {
  return (
    <FlexCol className="w-full gap-y-2.5">
      {fields.map((item, index) => (
        <Item key={item.id} item={item} disabled={false} index={index} />
      ))}
    </FlexCol>
  );
}

function Item({ item, disabled, index }: { item: FieldType; disabled: boolean; index: number }) {
  const { remove, update } = useAdditionalFieldsStore();

  const [openEditModal, setOpenEditModal] = useState(false);

  function toggleOpenEditModal(value: boolean) {
    setOpenEditModal(value);
  }

  function editFieldHandle(value: FieldType) {
    // изменить поле

    update(index, value);
  }

  function removeFieldHandle(value: number) {
    // изменить поле

    remove(value);
  }

  return (
    <FlexRow className="p-5 bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg cursor-pointer items-center" key={item.id}>
      <FlexCol className="w-full">
        <FlexRow className="justify-between">
          <FlexCol className="justify-center shrink">
            <h3 className="font-medium text-black">{item.name}</h3>
          </FlexCol>

          <FlexCol className=" text-[#637381] w-[4%]">
            {disabled ? null : (
              <FlexRow className="space-x-2.5">
                <FlexCol>
                  <TGButton
                    type="button"
                    iconOnly={true}
                    icon={<PencilIcon width={24} height={24} />}
                    className="w-6 h-10"
                    onClick={() => toggleOpenEditModal(true)}
                  />
                </FlexCol>
                <FlexCol>
                  <TGButton
                    type="button"
                    iconOnly={true}
                    icon={<TrashIcon width={24} height={24} />}
                    className="w-6 h-10"
                    onClick={() => removeFieldHandle(index)}
                  />
                </FlexCol>
              </FlexRow>
            )}
          </FlexCol>
        </FlexRow>

        <FlexRow className="text-[14px] text-primaryGray">
          {typeMap[item.type as keyof typeof typeMap]}
          {item.options?.length ? ` ${item.options.map((option) => option.name).join(", ")}` : ""}
        </FlexRow>
      </FlexCol>
      <EditModal
        open={openEditModal}
        toggleOpen={toggleOpenEditModal}
        onEditField={editFieldHandle}
        defaultField={item}
        typeModal="edit"
      />
    </FlexRow>
  );
}

function EditModal({
  open,
  toggleOpen,
  onEditField,
  defaultField,
}: {
  open: boolean;
  toggleOpen: (value: boolean) => void;
  onEditField: (value: FieldType) => void;

  typeModal?: string;
  defaultField?: FieldType;
}) {
  const [typeField, setTypeField] = useState<FieldType[]>([additionalList[0]]); //! изменить на 0
  const [nameField, setNameField] = useState("");
  const [options, setOptions] = useState<Option[]>([]);

  function changeTypeField(types: FieldType[]) {
    setTypeField(types);

    if (isSelectFields(typeField[0])) {
      setOptions([{ name: "", id: crypto.randomUUID() }]);
    } else {
      setOptions([]);
    }
  }

  function submitAdditionalField(name: string) {
    setNameField(name);
  }

  function returnFields() {
    changeField(defaultField);
  }

  function isChangedFields(defaultField: FieldType | undefined) {
    const isChangedName = defaultField?.name !== nameField;
    const isChangedType = defaultField?.type !== typeField[0].type;
    const isChangedOptions = isEqual(defaultField?.options, options);

    return isChangedName || isChangedType || !isChangedOptions;
  }

  function editField() {
    if (isChangedFields(defaultField)) {
      const field: FieldType = {
        id: defaultField ? defaultField.id : crypto.randomUUID(),
        name: nameField,
        type: typeField[0].type,
        value: typeField[0],
        options: isSelectFields(typeField[0]) ? options : [],
      };

      onEditField(field);
    }
  }

  function changeField(field: FieldType | undefined) {
    if (field) {
      const newDefaultType = getDefaultTypeField(field.type);

      setTypeField(newDefaultType);

      setNameField(field.name);

      if (!!field.options?.length) {
        setOptions(field.options);
      }
    }
  }

  function cancel() {
    returnFields();
    toggleOpen(false);
  }

  useEffect(() => {
    changeField(defaultField);
  }, [defaultField]);

  return (
    <Modal
      zIndex={9999}
      open={open}
      toggleDialog={cancel}
      modalClassName="min-h-0 custom-selects overflow-y-auto max-h-[90vh]"
    >
      <FlexCol className="shrink">
        <h1 className="px-10 text-black">Изменить дополнительное поле</h1>
        <div className="w-full py-5 px-10 my-[18px]">
          <div>
            <TGSelectPickerByList
              values={typeField}
              onChange={changeTypeField}
              fetchKey="additionalField"
              classNames={{
                selectClassName: "!px-5",
                labelClassName: "text-base font-normal",
                dropdownClassName: "w-full",
              }}
              searchPlaceholder="Выберите тип поля"
              dataField={{
                placeholder: "Выберите тип поля",
                type: "peoplePicker",
                require: true,
                title: "Тип поля",
                width: "w-full",
                key: "additionalField",
                single: true,
                hideRemoveButton: true,
              }}
              list={additionalList}
              errors={{}}
            />
          </div>
          <div className="w-full">
            <SearchUI
              name="searchType"
              search={false}
              delay={0}
              require={true}
              label="Название поля"
              placeholder="Введите название поля"
              defaultValue={nameField}
              onClick={submitAdditionalField}
            />
            {isSelectFields(typeField[0]) && (
              <div className="mt-5 text-primaryGray">
                <h4 className="mb-2.5">Вариант ответа</h4>
                <div className="p-5 rounded-lg bg-gray2">
                  <OptionsInput options={options} setOptions={(options) => setOptions(options)} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between px-10 pt-10 border-t">
          <TGButton type="button" onClick={cancel}>
            Отмена
          </TGButton>
          <TGButton
            type="button"
            color="bluedark"
            disabled={
              !typeField.length ||
              !nameField.length ||
              (isSelectFields(typeField[0]) && isEmptyOption(options)) ||
              !isChangedFields(defaultField)
            }
            onClick={editField}
          >
            Изменить поле
          </TGButton>
        </div>
      </FlexCol>
    </Modal>
  );
}
